import { useCallback, useEffect, useRef, useState } from "react";
import { CommunityAdminReviewModel } from "../../pages/communityAdmin/models/CommunityAdminReviewModel";
import { addComments, getComments, getCommunityDiscussion, getResources, toggleReact } from "../../services/communityService";
import { ApiArrayResponseModel } from "./models/ApiResponseModel";
import { ResourcesModel } from "./models/ResourcesModel";
import { CommentModel } from "./models/CommentModel";
import { DiscussDetailModel } from "./models/DiscussDetailModel";
import { CommunityDiscussComments } from "../../pages/community/models/CommunityDiscussComments";
import { CommunityMediaModel } from "../../pages/community/models/CommunityMediaModel";

export const useDiscussReview = (id?: string | number) => {
    const isMountedRef = useRef(true);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<Error | boolean | null>(null);
    const [details, setDetails] = useState<CommunityAdminReviewModel | null>(null);

    useEffect(() => {
            isMountedRef.current = true;
            return () => {
                isMountedRef.current = false;
            }
    }, []);

    const load = useCallback(async () => {
        if(!id)
            return;
        try {
            setLoading(true);
            // setDetails(null);
            setError(null);

            const [details, comments, resources] = await Promise.all([
                getCommunityDiscussion(id) as Promise<DiscussDetailModel | null>,
                getComments(id) as Promise<CommentModel[]>,
                getResources(id) as Promise<ApiArrayResponseModel<ResourcesModel>>
            ]);
            if(!isMountedRef.current)
                return;
            if(!details) {
                setError(true);
                return;
            }
            const _parsed: CommunityAdminReviewModel = {
                id: details.id,
                type: details.postType === 'circle' ? 'Mastermind Circle': 'Open Forum',
                author: {
                    avatar: details.authorAvatar,
                    username: details.fullName,
                    isMine: details.isMine ?? false
                },
                circles: {
                    quantity: details.invitesDetail?.length ?? 0,
                    previewAvatars: (details.invitesDetail ?? []).map((i) => i.avatar)
                },
                tags: details.tags,
                comments: comments.map(c => ({ 
                        id: c.id,
                        authorId: c.authorId,
                        author: c.authorName,
                        avatar: c.authorAvatar,
                        content: c.content,
                        createdAt: c.createdAt,
                        isMine: c.isHost,
                        isLiked: c.isLiked,
                        reactions: c.likesCount,
                        files: c.resources as any[],
                        reply: !!c.parentData ? {
                            id: c.parentData.id,
                            content: c.parentData.content,
                            author: c.parentData.fullName,
                            avatar: c.parentData.authorAvatar,
                            isMine: c.parentData.isMine
                        }: undefined
                    })) as CommunityDiscussComments[],
                mediafiles: resources.results.map(r => ({ 
                    id: r.id, 
                    filename: r.title, 
                    bytes: r.size, 
                    author: r.authorName, 
                    createdAt: r.createdAt, 
                    type: r.resourceType,
                    url: r.file 
                })) as CommunityMediaModel[],
                experiences: [],
                content: details.description,
                topic: details.topic,
                facilitator: details.facilitatorDetail ? {
                    avatar: details.facilitatorDetail.avatar,
                    username: details.facilitatorDetail.fullName,
                    isTeam: true
                }: undefined
            };
            setDetails(_parsed);
        }
        catch (error) {
            if(!isMountedRef.current)
                return;
            setError(error as Error ?? true);
        }
        finally {
            if(!isMountedRef.current)
                return;
            setLoading(false);
        }
    }, [id]);

    useEffect(() => {
        load();
    }, [load]);

    const addComment = useCallback(async (content: string, reply?: number, filesCb?: (commentId: number) => Promise<void>, postCb?: () => void) => {
        if(!id)
            return;
        try {
            setLoading(true);
            const _comment = await addComments(id, content, reply ?? null);
            if(filesCb && !!_comment.id)
                await filesCb(_comment.id);
            if(!!postCb) {
                postCb();
            }
            await load();
        }
        catch {

        }
        finally {
            if(!isMountedRef.current)
                return;
            setLoading(false);
        }
    }, []);

    const react = useCallback(async (comment_id: number | string) => {
        if(!id)
            return;
        try {
            setLoading(true);
            await toggleReact(id, comment_id);
            await load();
        }
        catch {

        }
        finally {
            if(!isMountedRef.current)
                return;
            setLoading(false);
        }
    }, []);


    return { loading, error, details, addComment, react }
}